/**
 * Phone Validation Hook
 * Provides phone number formatting for US phone numbers
 *
 * Validation is handled by Zod schemas (see src/schemas/orderFormSchema.ts)
 * This hook focuses only on formatting for better UX
 *
 * Usage:
 * ```tsx
 * const { format, inputProps } = usePhoneValidation();
 *
 * <input
 *   {...register('phone')}
 *   {...inputProps}
 *   onChange={(e) => {
 *     const formatted = format(e.target.value);
 *     setValue('phone', formatted);
 *   }}
 * />
 * ```
 */

import { useCallback } from 'react';

interface PhoneValidationResult {
  /**
   * Format a phone number as the user types
   * Returns formatted string: (555) 123-4567
   */
  format: (value: string) => string;

  /**
   * Mobile-optimized input props for phone number fields
   * Includes type, inputMode, autoComplete, and pattern
   */
  inputProps: {
    type: 'tel';
    inputMode: 'numeric';
    autoComplete: 'tel';
    pattern: '[0-9]*';
  };
}

/**
 * Hook for phone number formatting
 * Formats US phone numbers as (555) 123-4567
 *
 * Note: Validation is handled by Zod phoneSchema in orderFormSchema.ts
 */
export function usePhoneValidation(): PhoneValidationResult {
  /**
   * Format phone number as user types
   * Transforms input into (555) 123-4567 format
   */
  const format = useCallback((value: string): string => {
    if (!value) return '';

    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');

    // Limit to 10 digits
    const limitedDigits = digits.slice(0, 10);

    // Format based on number of digits
    if (limitedDigits.length <= 3) {
      return limitedDigits;
    } else if (limitedDigits.length <= 6) {
      return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;
    } else {
      return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
    }
  }, []);

  // Mobile-optimized input props
  const inputProps = {
    type: 'tel' as const,
    inputMode: 'numeric' as const,
    autoComplete: 'tel' as const,
    pattern: '[0-9]*' as const,
  };

  return {
    format,
    inputProps,
  };
}

/**
 * Extract raw digits from a formatted phone number
 * Useful for submitting to backend APIs
 *
 * @param formatted - Formatted phone number: (555) 123-4567
 * @returns Raw digits: 5551234567
 */
export function extractPhoneDigits(formatted: string): string {
  return formatted.replace(/\D/g, '');
}

/**
 * Check if a value looks like a phone number
 * Useful for conditional validation
 *
 * @param value - String to check
 * @returns True if the string contains phone-like digits (10+ digits)
 */
export function looksLikePhone(value: string): boolean {
  const digits = value.replace(/\D/g, '');
  return digits.length >= 10;
}
